<template>
  <div v-if="visible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">Add Agent</h3>
        <button @click="closeModal" class="modal-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <!-- Error Display -->
        <div v-if="errorMessage" class="error-alert">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <span>{{ errorMessage }}</span>
        </div>

        <div class="form-group">
          <label for="agentName" class="form-label">Agent Name</label>
          <input
            id="agentName"
            v-model="formData.name"
            type="text"
            class="form-input"
            placeholder="Enter agent name"
            required
          />
        </div>

        <div class="form-group" v-if="formData.agentRoleId != 3">
          <label for="department" class="form-label">Department</label>
          <select
            id="department"
            v-model="formData.departmentId"
            class="form-select"
            :required="formData.agentRoleId != 3"
          >
            <option value="">Select Department</option>
            <option
              v-for="dept in departments"
              :key="dept.id"
              :value="dept.id"
            >
              {{ dept.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="agentRole" class="form-label">Agent Role</label>
          <select
            id="agentRole"
            v-model="formData.agentRoleId"
            class="form-select"
            required
          >
            <option value="">Select Role</option>
            <option value="1">Customer Service Agent</option>
            <option value="3">Marketing Agent</option>
          </select>
          <div v-if="formData.agentRoleId === '1'" class="role-description">
            Handles inbound customer conversations across all connected channels. Has access to the chat inbox, leads, and their performance dashboard.
          </div>
          <div v-if="formData.agentRoleId === '3'" class="role-description role-description--marketing">
            Marketing-only access. Can create and manage WhatsApp templates, create broadcast campaigns, and view the customer list. Cannot access conversations or the chat inbox.
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="loading"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="loading || !formData.name || (formData.agentRoleId != 3 && !formData.departmentId) || !formData.agentRoleId"
          >
            <svg v-if="loading" class="btn-spinner" width="16" height="16" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            {{ loading ? 'Creating...' : 'Create Agent' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AddAgentModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    departments: {
      type: Array,
      default: () => []
    },
    errorMessage: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      formData: {
        name: '',
        departmentId: '',
        agentRoleId: '1'
      },
      loading: false
    }
  },
  watch: {
    visible (newVal) {
      if (!newVal) {
        this.resetForm()
      }
    },
    'formData.agentRoleId' (newVal) {
      if (parseInt(newVal) === 3) {
        this.formData.departmentId = ''
      }
    }
  },
  methods: {
    async handleSubmit () {
      const isMarketing = parseInt(this.formData.agentRoleId) === 3
      if (!this.formData.name || (!isMarketing && !this.formData.departmentId) || !this.formData.agentRoleId) {
        return
      }

      this.loading = true
      try {
        const result = {
          name: this.formData.name.trim(),
          departmentId: isMarketing ? null : parseInt(this.formData.departmentId),
          agentRoleId: parseInt(this.formData.agentRoleId)
        }

        this.$emit('save', result)
      } catch (error) {
        console.error('Error preparing form data:', error)
      } finally {
        this.loading = false
      }
    },
    closeModal () {
      this.resetForm()
      this.$emit('close')
    },
    resetForm () {
      this.formData.name = ''
      this.formData.departmentId = ''
      this.formData.agentRoleId = '1'
      this.loading = false
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0;
  border-bottom: 1px solid var(--border-primary);
  margin-bottom: 24px;
  padding-bottom: 16px;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-body {
  padding: 0 24px 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 6px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-select {
  cursor: pointer;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  justify-content: center;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .modal-header,
  .modal-body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}

.role-description {
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
}

.role-description--marketing {
  color: #2563eb;
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.25);
}

.dark .role-description--marketing {
  color: #93c5fd;
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.3);
}

/* Dark mode specific overrides */
.dark .form-input,
.dark .form-select {
  background: var(--bg-secondary);
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.dark .form-select option {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.dark .btn-secondary {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-color: var(--border-secondary);
}

.dark .btn-secondary:hover:not(:disabled) {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border-color: var(--border-primary);
}

/* Error Alert */
.error-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 20px;
  animation: slideDown 0.3s ease-out;
}

.error-alert svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.error-alert span {
  flex: 1;
  font-weight: 500;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dark .error-alert {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.4);
  color: #f87171;
}
</style>
